import { useState, useEffect } from 'react';
import { galleryAPI } from '../../utils/api';
import PublicLayout from '../../components/layout/PublicLayout';
import ReactPlayer from 'react-player';
import { DocumentIcon, MusicalNoteIcon, PlayCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import useSEO, { PAGE_SEO } from '../../hooks/useSEO';

const TYPES = [
  { key: null,    label: 'All'       },
  { key: 'image', label: 'Photos'    },
  { key: 'video', label: 'Videos'    },
  { key: 'audio', label: 'Audio'     },
  { key: 'pdf',   label: 'Documents' },
];

// ── Badge colour helper — no object literal in JSX ────────────
function typeBadgeClass(fileType) {
  if (fileType === 'image') return 'bg-cyan-400 text-navy-900';
  if (fileType === 'video') return 'bg-red-500 text-white';
  if (fileType === 'audio') return 'bg-purple-500 text-white';
  if (fileType === 'pdf')   return 'bg-gray-600 text-white';
  return 'bg-gray-500 text-white';
}

// ── Lightbox Modal ────────────────────────────────────────────
function LightboxModal({ item, onClose }) {
  if (!item) return null;

  // Close on Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="max-w-4xl w-full" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-white font-semibold truncate pr-4">
            {item.title || 'Gallery Item'}
          </h3>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white p-1 rounded-full hover:bg-white/10 flex-shrink-0"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {item.file_type === 'image' && (
          <img
            src={item.file_url}
            alt={item.title}
            className="w-full max-h-[75vh] object-contain rounded-xl"
          />
        )}
        {(item.file_type === 'video' || item.youtube_url) && (
          <div className="aspect-video rounded-xl overflow-hidden">
            <ReactPlayer
              url={item.youtube_url || item.file_url}
              width="100%" height="100%"
              controls playing
            />
          </div>
        )}
        {item.file_type === 'audio' && (
          <div className="bg-navy-800 rounded-xl p-8 text-center">
            <MusicalNoteIcon className="h-16 w-16 text-cyan-400 mx-auto mb-4" />
            <p className="text-white mb-4">{item.title}</p>
            <audio controls className="w-full" src={item.file_url} />
          </div>
        )}
        {item.file_type === 'pdf' && (
          <div className="bg-navy-800 rounded-xl p-8 text-center">
            <DocumentIcon className="h-16 w-16 text-cyan-400 mx-auto mb-4" />
            <p className="text-white mb-4">{item.title}</p>
            <a
              href={item.file_url}
              target="_blank"
              rel="noreferrer"
              className="inline-block bg-cyan-400 hover:bg-cyan-300 text-navy-900 font-bold px-6 py-2.5 rounded-xl transition-all"
            >
              Open PDF
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Gallery Card ──────────────────────────────────────────────
function GalleryCard({ item, onClick }) {
  const isVideo = item.file_type === 'video' || !!item.youtube_url;
  const isAudio = item.file_type === 'audio';
  const isPDF   = item.file_type === 'pdf';
  const badgeClass = typeBadgeClass(item.file_type);

  return (
    <div
      className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden cursor-pointer group hover:shadow-md hover:-translate-y-0.5 transition-all"
      onClick={() => onClick(item)}
    >
      <div className="h-44 relative overflow-hidden bg-navy-100">
        {item.file_type === 'image' ? (
          <img
            src={item.file_url}
            alt={item.title || ''}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : isVideo ? (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-navy-700 to-navy-900">
            <PlayCircleIcon className="h-14 w-14 text-white/80 group-hover:text-cyan-400 transition-colors" />
          </div>
        ) : isAudio ? (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-cyan-700 to-navy-900">
            <MusicalNoteIcon className="h-14 w-14 text-white/80 group-hover:text-cyan-300 transition-colors" />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-600 to-gray-900">
            <DocumentIcon className="h-14 w-14 text-white/80 group-hover:text-cyan-300 transition-colors" />
          </div>
        )}

        {/* Type badge */}
        <div className="absolute top-2 right-2">
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${badgeClass}`}>
            {(item.file_type || '').toUpperCase()}
          </span>
        </div>
      </div>

      {item.title && (
        <div className="px-4 py-3">
          <p className="text-sm font-medium text-navy-800 line-clamp-1">{item.title}</p>
          {item.album_title && (
            <p className="text-xs text-gray-400 mt-0.5">{item.album_title}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Gallery Page ──────────────────────────────────────────────
export default function GalleryPage() {
  useSEO(PAGE_SEO.gallery);

  const [items, setItems]           = useState([]);
  const [albums, setAlbums]         = useState([]);
  const [activeType, setActiveType] = useState(null);
  const [activeAlbum, setActiveAlbum] = useState(null);
  const [lightbox, setLightbox]     = useState(null);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    galleryAPI.getAlbums()
      .then(r => setAlbums(Array.isArray(r.data.data) ? r.data.data : []))
      .catch(() => setAlbums([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 60 };
    if (activeType)  params.file_type = activeType;
    if (activeAlbum) params.album_id  = activeAlbum;
    galleryAPI.getItems(params)
      .then(r => setItems(Array.isArray(r.data.data) ? r.data.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [activeType, activeAlbum]);

  return (
    <PublicLayout>
      {lightbox && (
        <LightboxModal item={lightbox} onClose={() => setLightbox(null)} />
      )}

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="mb-8">
          <h1 className="section-title mb-2">Gallery</h1>
          <p className="text-gray-500">Photos, videos, audio recordings and documents</p>
        </div>

        {/* Album filter */}
        {albums.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-1 mb-4 scrollbar-hide">
            <button
              onClick={() => setActiveAlbum(null)}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                !activeAlbum
                  ? 'bg-navy-700 text-white border-navy-700'
                  : 'border-gray-200 text-gray-600 hover:border-navy-300'
              }`}
            >
              All Albums
            </button>
            {albums.map(a => (
              <button
                key={a.id}
                onClick={() => setActiveAlbum(a.id)}
                className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                  activeAlbum === a.id
                    ? 'bg-navy-700 text-white border-navy-700'
                    : 'border-gray-200 text-gray-600 hover:border-navy-300'
                }`}
              >
                {a.title}
              </button>
            ))}
          </div>
        )}

        {/* Type filter */}
        <div className="flex gap-2 mb-8 flex-wrap">
          {TYPES.map(({ key, label }) => (
            <button
              key={String(key)}
              onClick={() => setActiveType(key)}
              className={`px-5 py-2 rounded-xl text-sm font-semibold transition-all border ${
                activeType === key
                  ? 'bg-cyan-400 text-navy-900 border-cyan-400'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-cyan-300'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-52 bg-gray-100 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-5xl mb-4">🖼</div>
            <p className="text-gray-400 font-medium">No gallery items yet.</p>
            <p className="text-gray-300 text-sm mt-1">Check back soon, in sha Allah.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map(item => (
              <GalleryCard
                key={item.id}
                item={item}
                onClick={setLightbox}
              />
            ))}
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
